import React from 'react';
import { createPagesApi } from '@/pages-wip';
import _ from 'lodash';
import { Button } from '@wix/wix-base-ui';
import { translate } from '#packages/i18n';
import { TRANSLATIONS_MAP } from '../utils/translations';
import { findExistingItem } from '../../../utils/utils';

import {
  DropMode,
  type IMenuItem,
  type IOnDrop,
  type ITreeItem,
  MenuList as BaseUIMenuList,
  type IMenuId,
  type IMenuItemId,
  type IAction,
  type ICheckCanDrop,
  type IMenuItemProps,
  menuItemTree,
  type IMenuItemSuffix,
  type IGetMenuItemLocalizations,
} from '#packages/baseUI';
import {
  fedopsLogger,
  hoc,
  link,
  functionUtils,
  isAdvancedMenuOpen,
} from '#packages/util';

import {
  createMenuApi,
  type IMenuPages,
  type IMenuRoutes,
} from '../../../API/menuAPI';
import { cleanId, filterVisibleMenuItems } from '../../../utils/utils';
import {
  getCollapsedItems,
  setItemCollapsed,
} from '../../../API/userPreferences';
import { getSymbolName } from '../menuItemSymbols';

import type { EditorAPI } from '#packages/editorAPI';
import type {
  PagesData,
  MenuItem as DSMenuItem,
} from '@wix/document-services-types';
import type { StateMapperArgs } from 'types/redux';
import { menuBiLogger } from '../../../bi/menuBiLogger';
import { AUTOMATION_IDS } from '../utils/automationIds';
import {
  createSlotContainerItem,
  createSlotPlaceholderItem,
  getSlotItemParentId,
  isSlotParentItem,
  isSlotItemById,
} from '../utils/slotUtils';
import * as menuUtils from '../../../utils/utils';
import { type IMenuConfig } from '@/menu';

const { createWeakMemo } = functionUtils;

const {
  connect,
  STORES: { EDITOR_API },
} = hoc;

const {
  removeFromTree,
  insertAfter,
  insertBefore,
  findItem,
  flattenTree,
  getNestingLevel,
  hasSubnestedItems,
  findParent,
} = menuItemTree;

const {
  linkTypeValidators: { isPageLink, isDynamicPageLink },
} = link;

interface IOwnProps {
  menuId: IMenuId;
  checkCanDrop: ICheckCanDrop<IMenuItemProps, IMenuItem>;
  getActions(item: ITreeItem<IMenuItem>): IAction[];
  getSuffixes?(item: ITreeItem<IMenuItem>): IMenuItemSuffix[];
  setEditingId: (id: IMenuItemId) => void;
  editingId?: IMenuItemId;
  selectedId?: IMenuItemId;
  onItemClick?(id: IMenuItemId): void;
  onItemDoubleClick?(id: IMenuItemId): void;
  onRename(id: IMenuItemId, newName: string): void;
  getItemLocalizations: IGetMenuItemLocalizations;
  maxItemNameLength?: number;
  blockDrag?: boolean;
  editContainer?(item: DSMenuItem): void;
  config: IMenuConfig;
}

interface IStoreProps {
  menuItems: ITreeItem<IMenuItem>[];
  collapsedIds: IMenuItemId[];
  getSuffixes?(item: ITreeItem<IMenuItem>): IMenuItemSuffix[];
}

interface IDispatchProps {
  onDrop: IOnDrop;
  toggleCollapse(id: IMenuItemId): void;
}

type IConnectedMenuListProps = IOwnProps & IStoreProps & IDispatchProps;

export const MenuList = (props: IConnectedMenuListProps) => {
  const {
    menuItems,
    onDrop,
    toggleCollapse,
    collapsedIds,
    getActions,
    getSuffixes,
    checkCanDrop,
    onRename,
    editingId,
    setEditingId,
    selectedId,
    onItemClick,
    onItemDoubleClick,
    getItemLocalizations,
    maxItemNameLength,
    blockDrag,
  } = props;

  const handleRenameCancel = () => {
    setEditingId(null);
    fedopsLogger.interactionEnded(
      fedopsLogger.INTERACTIONS.MANAGE_MENU.RENAME_ITEM,
    );
  };

  const handleGetActions = (item: ITreeItem<IMenuItem>) => getActions(item);

  const handleRename = (id: IMenuItemId, newName: string) => {
    onRename(id, newName);
    setEditingId(null);
    fedopsLogger.interactionEnded(
      fedopsLogger.INTERACTIONS.MANAGE_MENU.RENAME_ITEM,
    );
  };

  return (
    <BaseUIMenuList
      blockDrag={blockDrag}
      items={menuItems}
      collapsedIds={collapsedIds}
      editingId={editingId}
      onDrop={onDrop}
      getActions={handleGetActions}
      getSuffixes={getSuffixes}
      toggleCollapse={toggleCollapse}
      onRename={handleRename}
      onRenameCancel={handleRenameCancel}
      onContextMenuOpen={menuBiLogger.logMenuItemContextMenuOpened}
      checkCanDrop={checkCanDrop}
      onItemDoubleClick={onItemDoubleClick}
      onItemClick={onItemClick}
      selectedMenuItemId={selectedId}
      getItemLocalizations={getItemLocalizations}
      maxItemNameLength={maxItemNameLength}
      itemAutomationId={AUTOMATION_IDS.ITEM.SELF}
    />
  );
};

const createMenuItemsConverter = (
  homePageId: string,
  pagesData: IMenuPages,
  routersData: IMenuRoutes,
  editContainer?: (item: DSMenuItem) => void,
) => {
  const isStructuredMegaMenuFeatureEnabled =
    menuUtils.isStructuredMegaMenuFeatureEnabled();

  type ConvertMenuItems = (items: DSMenuItem[]) => IMenuItem[];

  const getItems = (item: DSMenuItem, convertMenuItems: ConvertMenuItems) => {
    const isItemSlotContainer = isSlotParentItem(item);

    if (!isItemSlotContainer) {
      return convertMenuItems(item.items);
    }

    if (isStructuredMegaMenuFeatureEnabled) {
      return item.items.length === 0
        ? [createSlotPlaceholderItem(item)]
        : convertMenuItems(item.items);
    }

    return [createSlotContainerItem(item, editContainer)];
  };

  const convertMenuItems: ConvertMenuItems = (menuItems) =>
    menuItems.map((item) => {
      const page: PagesData = isPageLink(item.link)
        ? pagesData[cleanId(item.link.pageId)]
        : undefined;
      const router = isDynamicPageLink(item.link)
        ? routersData[item.link.routerId]
        : undefined;

      const isItemSlotContainer = isSlotParentItem(item);

      return {
        ...item,
        symbol: getSymbolName(item, homePageId, page, router),
        items: getItems(item, convertMenuItems),
        ...(isItemSlotContainer && isStructuredMegaMenuFeatureEnabled
          ? {
              customButton: (
                <Button
                  className="btn-confirm-secondary btn-sm menu-manage-panel__slot-custom-button"
                  onClick={() => editContainer(item)}
                >
                  {translate(TRANSLATIONS_MAP.ITEM.CONTAINER.edit)}
                </Button>
              ),
              showCustomButtonWithContextMenu: true,
            }
          : {}),
      };
    });

  return convertMenuItems;
};

const suffixesCache = new WeakMap();

const mapStateToProps = (
  { editorAPI }: StateMapperArgs,
  ownProps: IOwnProps,
): IStoreProps => {
  const menuAPI = createMenuApi(editorAPI);
  const pagesAPI = createPagesApi(editorAPI);

  const { menuId } = ownProps;

  const currentMenu = menuAPI.getMenu(menuId);
  const menuItems = currentMenu?.items || [];
  const homepageId = pagesAPI.getHomePageId();

  const pagesData = menuAPI.getMenuPagesInfo(menuId);
  const routersData = menuAPI.getMenuRoutersInfo(menuId);

  const convertedMenuItems = _.flow(
    filterVisibleMenuItems,
    createMenuItemsConverter(
      homepageId,
      pagesData,
      routersData,
      ownProps.editContainer,
    ),
  )(menuItems, editorAPI.isMobileEditor());

  const convertedItemsTree = flattenTree(convertedMenuItems as DSMenuItem[]);

  const collapsedIds = getCollapsedItems(editorAPI, menuId);

  const subnestingWarningContents = menuAPI.getSubnestingSupportExcuse();
  const isSubnestingSupportedByComponent = !subnestingWarningContents;

  const warningSuffix = {
    tooltipText: subnestingWarningContents?.content,
    symbolName: 'warning',
    helpText: subnestingWarningContents?.linkText,
    openHelpCenter: () =>
      editorAPI.panelManager.openHelpCenter(subnestingWarningContents.helpId),
  };

  const tinyWarningSuffix = { symbolName: 'warning_tiny' };

  const getSuffixes = (item: ITreeItem<IMenuItem>): IMenuItemSuffix[] => {
    let suffixes = ownProps.getSuffixes?.(item) || [];

    if (isSubnestingSupportedByComponent || !isAdvancedMenuOpen()) {
      return suffixes;
    }

    const nestingLevel = getNestingLevel(currentMenu.items, item);
    if (nestingLevel === 2) {
      suffixes = [...suffixes, warningSuffix];
    }

    if (nestingLevel < 2 && hasSubnestedItems(currentMenu.items, item)) {
      suffixes = [...suffixes, tinyWarningSuffix];
    }

    return suffixes;
  };

  // use cache outside mapStateToProps
  const cashedGetSuffixes = createWeakMemo(getSuffixes, suffixesCache);

  return {
    menuItems: convertedItemsTree as ITreeItem<IMenuItem>[],
    getSuffixes: cashedGetSuffixes,
    collapsedIds,
  };
};

const getEditorAPI = (
  dispatch: AnyFixMe,
  getState: AnyFixMe,
  { editorAPI }: AnyFixMe,
) => editorAPI;

const mapDispatchToProps = (
  dispatch: AnyFixMe,
  ownProps: IOwnProps,
): IDispatchProps => {
  const editorAPI: EditorAPI = dispatch(getEditorAPI);
  const { menuId, config } = ownProps;
  const menuAPI = createMenuApi(editorAPI, config);
  const { replaceMenuItems, moveToDropdown } = menuAPI;

  const callHooksOnInsertion = <T extends DSMenuItem>(
    prevTree: T[],
    nextTree: T[],
    item: T,
  ) => {
    const prevParent = findParent(prevTree, item.id);
    const nextParent = findParent(nextTree, item.id);

    config.hooks?.onItemMoved?.(menuId, {
      item,
      prevParent: findExistingItem(prevParent, nextTree),
      nextParent,
    });
  };

  const onDrop: IOnDrop = (
    droppedItem,
    itemDropOnId,
    dropMode = DropMode.on,
  ) => {
    fedopsLogger.interactionStarted(
      fedopsLogger.INTERACTIONS.MANAGE_MENU.DND_MOVE_ITEM,
    );
    const { items } = menuAPI.getMenu(menuId);
    const item = menuAPI.getItem(menuId, droppedItem.id) as DSMenuItem;
    const targetId =
      itemDropOnId && isSlotItemById(itemDropOnId)
        ? getSlotItemParentId(itemDropOnId)
        : itemDropOnId;
    const dropTarget = targetId && findItem(items, targetId);

    menuBiLogger.logMenuItemDrop(
      dropMode,
      items,
      item,
      dropTarget as DSMenuItem,
    );

    if (!dropTarget) {
      replaceMenuItems(menuId, [...removeFromTree(items, item.id), item]);
      fedopsLogger.interactionEnded(
        fedopsLogger.INTERACTIONS.MANAGE_MENU.DND_MOVE_ITEM,
      );
      editorAPI.history.add('menu items reorder');
      return;
    }

    let newTree: DSMenuItem[];

    switch (dropMode) {
      case DropMode.on:
        moveToDropdown(menuId, item.id, targetId);
        break;
      case DropMode.after:
        newTree = removeFromTree(items, item.id);
        newTree = insertAfter(newTree, targetId, item);
        replaceMenuItems(menuId, newTree);
        callHooksOnInsertion(items, newTree, item);
        break;
      case DropMode.before:
        newTree = removeFromTree(items, item.id);
        newTree = insertBefore(newTree, targetId, item);
        replaceMenuItems(menuId, newTree);
        callHooksOnInsertion(items, newTree, item);
        break;
    }

    editorAPI.history.add('menu items reorder');

    fedopsLogger.interactionEnded(
      fedopsLogger.INTERACTIONS.MANAGE_MENU.DND_MOVE_ITEM,
    );
  };

  const toggleCollapse = (id: IMenuItemId) => {
    const isCollapsed = getCollapsedItems(editorAPI, menuId).includes(id);

    const options = {
      menuId,
      itemId: id,
      isCollapsed: !isCollapsed,
    };

    setItemCollapsed(editorAPI, options);
  };

  return {
    onDrop,
    toggleCollapse,
  };
};

export const ConnectedMenuList = connect(
  EDITOR_API,
  mapStateToProps,
  mapDispatchToProps,
)(MenuList);
